import {
  createReportScheduleSchema,
  REPORT_FORMAT_LABELS,
  REPORT_FORMATS,
  REPORT_FREQUENCIES,
  REPORT_FREQUENCY_LABELS,
  REPORT_KEY_LABELS,
  REPORT_RUN_STATUS_LABELS,
  type ReportFormat,
  type ReportFrequency,
  type ReportKey,
  type ReportRunStatus,
  type ReportSchedule,
} from "@station/contracts";
import {
  Alert,
  Button,
  Checkbox,
  ConfirmDialog,
  Dialog,
  Drawer,
  EmptyState,
  Input,
  Label,
  PageHeader,
  Select,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Pause, Pencil, Play, Plus, Send, Archive, History } from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useRecipientCandidates,
  useReportCatalog,
  useReportSchedules,
  useScheduleMutations,
  useScheduleRuns,
} from "../../../features/reports/queries.js";
import { ReportsGate, TableScroll, useScopeOptions } from "../../../features/reports/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../lib/format.js";
import { useDocumentTitle } from "../../../lib/hooks.js";

export const Route = createFileRoute("/_app/raporlar/zamanlanmis")({ component: SchedulesRoute });

const WEEKDAYS = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"] as const;
const RUN_TONES: Record<ReportRunStatus, "success" | "warning" | "danger" | "neutral"> = {
  SENT: "success",
  PARTIAL: "warning",
  FAILED: "danger",
  SKIPPED: "neutral",
};

/**
 * Zamanlanmış raporlar (Prompt 22, ADR 0022 §15): günlük / haftalık / aylık Excel ya da PDF raporu kiracı üyelerine e-postayla.
 * Rapor HER ALICI İÇİN KENDİ yetkisiyle üretilir (göremeyeceği istasyon verisi ona gitmez; izni olmayan alıcı atlanır ve
 * koşu geçmişinde nedeni görünür). Kullanıcı kendi zamanlamalarını, rapor ayarları yöneticisi kiracının tümünü yönetir;
 * başka bir yönetici düzenler / sürdürürse sahipliği devralır.
 */
function SchedulesRoute(): ReactElement {
  const { t } = useTranslation();
  useDocumentTitle(t("menu.reportsSchedules"));
  return (
    <ReportsGate need="schedule">
      <Schedules />
    </ReportsGate>
  );
}

function scheduleWhen(
  schedule: Pick<ReportSchedule, "frequency" | "timeOfDay" | "weekday" | "monthDay">,
): string {
  if (schedule.frequency === "WEEKLY")
    return `Her ${WEEKDAYS[(schedule.weekday ?? 1) - 1]} ${schedule.timeOfDay}`;
  if (schedule.frequency === "MONTHLY") return `Her ayın ${schedule.monthDay}. günü ${schedule.timeOfDay}`;
  return `Her gün ${schedule.timeOfDay}`;
}

function Th({ children, align = "left" }: { children: ReactNode; align?: "left" | "right" }): ReactElement {
  return (
    <th scope="col" className={`px-3 py-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}>
      {children}
    </th>
  );
}

function Schedules(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const schedules = useReportSchedules();
  const mutations = useScheduleMutations();
  const [editing, setEditing] = useState<ReportSchedule | "new" | null>(null);
  const [runsOf, setRunsOf] = useState<ReportSchedule | null>(null);
  const [archiving, setArchiving] = useState<ReportSchedule | null>(null);

  const act = (schedule: ReportSchedule, action: "pause" | "resume") =>
    mutations.action.mutate(
      { schedule, action },
      {
        onSuccess: () =>
          toast({
            tone: "success",
            title: t(action === "pause" ? "reports.schedules.pausedToast" : "reports.schedules.resumedToast"),
          }),
        onError: (caught) =>
          toast({ tone: "danger", title: t("reports.schedules.failed"), description: errorMessage(caught) }),
      },
    );
  const runNow = (schedule: ReportSchedule) =>
    mutations.runNow.mutate(schedule, {
      onSuccess: (run) =>
        toast({
          tone: run.status === "SENT" ? "success" : "info",
          title: t("reports.schedules.ran", { status: REPORT_RUN_STATUS_LABELS[run.status] }),
          description: t("reports.schedules.ranDetail", {
            sent: run.recipients.filter((recipient) => recipient.status === "SENT").length,
            total: run.recipients.length,
          }),
        }),
      onError: (caught) =>
        toast({ tone: "danger", title: t("reports.schedules.failed"), description: errorMessage(caught) }),
    });

  return (
    <>
      <PageHeader
        title={t("menu.reportsSchedules")}
        description={t("reports.schedules.description")}
        actions={
          <Button onClick={() => setEditing("new")}>
            <Plus aria-hidden="true" />
            {t("reports.schedules.new")}
          </Button>
        }
      />
      {schedules.error ? <Alert tone="danger">{errorMessage(schedules.error)}</Alert> : null}
      {schedules.isPending ? (
        <SkeletonRows rows={4} />
      ) : (schedules.data ?? []).length === 0 ? (
        <EmptyState
          title={t("reports.schedules.emptyTitle")}
          description={t("reports.schedules.emptyDescription")}
          action={<Button onClick={() => setEditing("new")}>{t("reports.schedules.new")}</Button>}
        />
      ) : (
        <TableScroll label={t("menu.reportsSchedules")}>
          <table className="w-full min-w-[1100px] text-sm">
            <caption className="sr-only">{t("menu.reportsSchedules")}</caption>
            <thead className="bg-muted/50">
              <tr>
                <Th>{t("reports.schedules.name")}</Th>
                <Th>{t("reports.schedules.report")}</Th>
                <Th>{t("reports.schedules.when")}</Th>
                <Th>{t("reports.schedules.recipients")}</Th>
                <Th>{t("reports.schedules.next")}</Th>
                <Th>{t("reports.schedules.last")}</Th>
                <Th>{t("reports.schedules.owner")}</Th>
                <Th align="right">{t("common.actions")}</Th>
              </tr>
            </thead>
            <tbody>
              {(schedules.data ?? []).map((schedule) => (
                <tr key={schedule.id} className="border-t align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium">{schedule.name}</div>
                    {!schedule.isActive ? (
                      <StatusBadge tone="warning">
                        {schedule.pausedReason ?? t("reports.schedules.paused")}
                      </StatusBadge>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    {REPORT_KEY_LABELS[schedule.reportKey]}
                    <div className="text-xs text-muted-foreground">
                      {REPORT_FORMAT_LABELS[schedule.format]}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {scheduleWhen(schedule)}
                    <div className="text-xs text-muted-foreground">
                      {REPORT_FREQUENCY_LABELS[schedule.frequency]}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {schedule.recipients.map((recipient) => recipient.fullName).join(", ")}
                  </td>
                  <td className="px-3 py-2">
                    {schedule.nextRunAt ? formatDateTime(schedule.nextRunAt) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {schedule.lastRunStatus ? (
                      <StatusBadge tone={RUN_TONES[schedule.lastRunStatus]}>
                        {REPORT_RUN_STATUS_LABELS[schedule.lastRunStatus]}
                      </StatusBadge>
                    ) : (
                      "—"
                    )}
                    {schedule.lastRunAt ? (
                      <div className="text-xs text-muted-foreground">
                        {formatDateTime(schedule.lastRunAt)}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">{schedule.owner.fullName}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap justify-end gap-1">
                      <IconButton
                        label={t("reports.schedules.history", { name: schedule.name })}
                        onClick={() => setRunsOf(schedule)}
                      >
                        <History />
                      </IconButton>
                      {schedule.canEdit ? (
                        <>
                          <IconButton
                            label={t("reports.schedules.runNow", { name: schedule.name })}
                            onClick={() => runNow(schedule)}
                            busy={
                              mutations.runNow.isPending && mutations.runNow.variables?.id === schedule.id
                            }
                          >
                            <Send />
                          </IconButton>
                          <IconButton
                            label={t("reports.schedules.edit", { name: schedule.name })}
                            onClick={() => setEditing(schedule)}
                          >
                            <Pencil />
                          </IconButton>
                          {schedule.isActive ? (
                            <IconButton
                              label={t("reports.schedules.pause", { name: schedule.name })}
                              onClick={() => act(schedule, "pause")}
                            >
                              <Pause />
                            </IconButton>
                          ) : (
                            <IconButton
                              label={t("reports.schedules.resume", { name: schedule.name })}
                              onClick={() => act(schedule, "resume")}
                            >
                              <Play />
                            </IconButton>
                          )}
                          <IconButton
                            label={t("reports.schedules.archive", { name: schedule.name })}
                            onClick={() => setArchiving(schedule)}
                          >
                            <Archive />
                          </IconButton>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      <ScheduleDialog schedule={editing} onClose={() => setEditing(null)} />
      <RunsDrawer schedule={runsOf} onClose={() => setRunsOf(null)} />
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => (open ? null : setArchiving(null))}
        title={t("reports.schedules.archiveTitle")}
        description={t("reports.schedules.archiveDescription", { name: archiving?.name ?? "" })}
        confirmLabel={t("reports.schedules.archiveConfirm")}
        tone="danger"
        onConfirm={async () => {
          if (!archiving) return;
          await mutations.action.mutateAsync({ schedule: archiving, action: "archive" });
          toast({ tone: "success", title: t("reports.schedules.archived") });
          setArchiving(null);
        }}
      />
    </>
  );
}

function IconButton({
  label,
  onClick,
  busy = false,
  children,
}: {
  label: string;
  onClick: () => void;
  busy?: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <Button variant="ghost" size="icon" aria-label={label} title={label} onClick={onClick} loading={busy}>
      {children}
    </Button>
  );
}

// ── Oluştur / düzenle ─────────────────────────────────────────────────────────
interface Draft {
  name: string;
  reportKey: ReportKey;
  format: ReportFormat;
  frequency: ReportFrequency;
  timeOfDay: string;
  weekday: number;
  monthDay: number;
  companyId: string;
  stationIds: string[];
  stationId: string;
  recipientUserIds: string[];
}

const emptyDraft = (): Draft => ({
  name: "",
  reportKey: "DAILY_SALES",
  format: "XLSX",
  frequency: "DAILY",
  timeOfDay: "07:30",
  weekday: 1,
  monthDay: 1,
  companyId: "",
  stationIds: [],
  stationId: "",
  recipientUserIds: [],
});

function draftOf(schedule: ReportSchedule): Draft {
  const params = schedule.params as { companyId?: string; stationIds?: string[]; stationId?: string };
  return {
    name: schedule.name,
    reportKey: schedule.reportKey,
    format: schedule.format,
    frequency: schedule.frequency,
    timeOfDay: schedule.timeOfDay,
    weekday: schedule.weekday ?? 1,
    monthDay: schedule.monthDay ?? 1,
    companyId: params.companyId ?? "",
    stationIds: params.stationIds ?? [],
    stationId: params.stationId ?? "",
    recipientUserIds: schedule.recipients.map((recipient) => recipient.userId),
  };
}

function paramsOf(draft: Draft): Record<string, unknown> {
  const company = draft.companyId ? { companyId: draft.companyId } : {};
  switch (draft.reportKey) {
    case "DAILY_SALES":
    case "STATION_COMPARISON":
      return { ...company, stationIds: draft.stationIds };
    case "FUEL_ANALYTICS":
      return { ...company, ...(draft.stationId ? { stationId: draft.stationId } : {}) };
    default:
      return company;
  }
}

function ScheduleDialog({
  schedule,
  onClose,
}: {
  schedule: ReportSchedule | "new" | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const catalog = useReportCatalog(schedule !== null);
  const candidates = useRecipientCandidates(schedule !== null);
  const options = useScopeOptions();
  const mutations = useScheduleMutations();
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);
  const ids = {
    name: useId(),
    report: useId(),
    format: useId(),
    frequency: useId(),
    time: useId(),
    weekday: useId(),
    monthDay: useId(),
    company: useId(),
    station: useId(),
  };

  useEffect(() => {
    if (schedule === null) return;
    setDraft(schedule === "new" ? emptyDraft() : draftOf(schedule));
    setError(null);
  }, [schedule]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((previous) => ({ ...previous, [key]: value }));
  const reports = (catalog.data?.items ?? []).filter((item) => item.available);
  const stations = options.stations.filter(
    (station) => !draft.companyId || station.companyId === draft.companyId,
  );
  const busy = mutations.create.isPending || mutations.replace.isPending;

  async function submit(): Promise<void> {
    const input = {
      name: draft.name,
      reportKey: draft.reportKey,
      params: paramsOf(draft),
      format: draft.format,
      frequency: draft.frequency,
      timeOfDay: draft.timeOfDay,
      weekday: draft.frequency === "WEEKLY" ? draft.weekday : null,
      monthDay: draft.frequency === "MONTHLY" ? draft.monthDay : null,
      recipientUserIds: draft.recipientUserIds,
    };
    const parsed = createReportScheduleSchema.safeParse(input);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("reports.schedules.invalid"));
      return;
    }
    setError(null);
    try {
      if (schedule === "new") await mutations.create.mutateAsync(parsed.data);
      else if (schedule)
        await mutations.replace.mutateAsync({
          id: schedule.id,
          body: { ...parsed.data, version: schedule.version },
        });
      toast({ tone: "success", title: t("reports.schedules.saved") });
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <Dialog
      open={schedule !== null}
      onOpenChange={(open) => (open ? null : onClose())}
      title={schedule === "new" ? t("reports.schedules.newTitle") : t("reports.schedules.editTitle")}
      description={t("reports.schedules.dialogHint")}
      className="max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={() => void submit()} loading={busy}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form
        className="grid max-h-[65vh] gap-4 overflow-y-auto pr-1"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="grid gap-1.5">
          <Label htmlFor={ids.name}>{t("reports.schedules.name")}</Label>
          <Input
            id={ids.name}
            value={draft.name}
            maxLength={120}
            onChange={(event) => set("name", event.target.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor={ids.report}>{t("reports.schedules.report")}</Label>
            <Select
              id={ids.report}
              value={draft.reportKey}
              onChange={(event) =>
                setDraft((previous) => ({
                  ...previous,
                  reportKey: event.target.value as ReportKey,
                  stationIds: [],
                  stationId: "",
                }))
              }
            >
              {reports.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={ids.format}>{t("reports.schedules.format")}</Label>
            <Select
              id={ids.format}
              value={draft.format}
              onChange={(event) => set("format", event.target.value as ReportFormat)}
            >
              {REPORT_FORMATS.map((format) => (
                <option key={format} value={format}>
                  {REPORT_FORMAT_LABELS[format]}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={ids.frequency}>{t("reports.schedules.frequency")}</Label>
            <Select
              id={ids.frequency}
              value={draft.frequency}
              onChange={(event) => set("frequency", event.target.value as ReportFrequency)}
            >
              {REPORT_FREQUENCIES.map((frequency) => (
                <option key={frequency} value={frequency}>
                  {REPORT_FREQUENCY_LABELS[frequency]}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={ids.time}>{t("reports.schedules.time")}</Label>
            <Input
              id={ids.time}
              type="time"
              value={draft.timeOfDay}
              onChange={(event) => set("timeOfDay", event.target.value)}
            />
          </div>
          {draft.frequency === "WEEKLY" ? (
            <div className="grid gap-1.5">
              <Label htmlFor={ids.weekday}>{t("reports.schedules.weekday")}</Label>
              <Select
                id={ids.weekday}
                value={String(draft.weekday)}
                onChange={(event) => set("weekday", Number(event.target.value))}
              >
                {WEEKDAYS.map((label, index) => (
                  <option key={label} value={index + 1}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          {draft.frequency === "MONTHLY" ? (
            <div className="grid gap-1.5">
              <Label htmlFor={ids.monthDay}>{t("reports.schedules.monthDay")}</Label>
              <Select
                id={ids.monthDay}
                value={String(draft.monthDay)}
                onChange={(event) => set("monthDay", Number(event.target.value))}
              >
                {Array.from({ length: 28 }, (_unused, index) => (
                  <option key={index + 1} value={index + 1}>
                    {index + 1}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          {options.companies.length > 1 || draft.reportKey === "PROFITABILITY" ? (
            <div className="grid gap-1.5">
              <Label htmlFor={ids.company}>{t("reports.filter.company")}</Label>
              <Select
                id={ids.company}
                value={draft.companyId}
                onChange={(event) =>
                  setDraft((previous) => ({
                    ...previous,
                    companyId: event.target.value,
                    stationIds: [],
                    stationId: "",
                  }))
                }
              >
                <option value="">{t("reports.filter.allCompanies")}</option>
                {options.companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.shortName ?? company.legalName}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          {draft.reportKey === "FUEL_ANALYTICS" && stations.length > 1 ? (
            <div className="grid gap-1.5">
              <Label htmlFor={ids.station}>{t("reports.filter.station")}</Label>
              <Select
                id={ids.station}
                value={draft.stationId}
                onChange={(event) => set("stationId", event.target.value)}
              >
                <option value="">{t("reports.filter.allStations")}</option>
                {stations.map((station) => (
                  <option key={station.id} value={station.id}>
                    {station.code} · {station.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
        </div>
        {(draft.reportKey === "DAILY_SALES" || draft.reportKey === "STATION_COMPARISON") &&
        stations.length > 1 ? (
          <fieldset className="grid gap-2">
            <legend className="text-sm font-medium">{t("reports.filter.stations")}</legend>
            <p className="text-xs text-muted-foreground">{t("reports.schedules.stationsHint")}</p>
            <div className="grid gap-1 sm:grid-cols-2">
              {stations.map((station) => (
                <label key={station.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={draft.stationIds.includes(station.id)}
                    onChange={(event) =>
                      set(
                        "stationIds",
                        event.target.checked
                          ? [...draft.stationIds, station.id]
                          : draft.stationIds.filter((id) => id !== station.id),
                      )
                    }
                  />
                  {station.code} · {station.name}
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}
        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium">{t("reports.schedules.recipients")}</legend>
          <p className="text-xs text-muted-foreground">{t("reports.schedules.recipientsHint")}</p>
          <div className="grid max-h-48 gap-1 overflow-y-auto rounded-md border p-2 sm:grid-cols-2">
            {(candidates.data ?? []).map((person) => (
              <label key={person.userId} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={draft.recipientUserIds.includes(person.userId)}
                  onChange={(event) =>
                    set(
                      "recipientUserIds",
                      event.target.checked
                        ? [...draft.recipientUserIds, person.userId]
                        : draft.recipientUserIds.filter((id) => id !== person.userId),
                    )
                  }
                />
                {person.fullName}
              </label>
            ))}
          </div>
        </fieldset>
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </form>
    </Dialog>
  );
}

// ── Koşu geçmişi ──────────────────────────────────────────────────────────────
function RunsDrawer({
  schedule,
  onClose,
}: {
  schedule: ReportSchedule | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const runs = useScheduleRuns(schedule?.id ?? null);
  return (
    <Drawer
      open={schedule !== null}
      onOpenChange={(open) => (open ? null : onClose())}
      title={t("reports.schedules.historyTitle", { name: schedule?.name ?? "" })}
      description={t("reports.schedules.historyDescription")}
    >
      {runs.error ? <Alert tone="danger">{errorMessage(runs.error)}</Alert> : null}
      {runs.isPending ? (
        <SkeletonRows rows={4} />
      ) : (runs.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("reports.schedules.noRuns")}</p>
      ) : (
        <ol className="grid gap-3">
          {(runs.data ?? []).map((run) => (
            <li key={run.id} className="grid gap-2 rounded-md border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">
                  {run.periodFrom === run.periodTo
                    ? formatDate(run.periodFrom)
                    : `${formatDate(run.periodFrom)} – ${formatDate(run.periodTo)}`}
                </span>
                <StatusBadge tone={RUN_TONES[run.status]}>{REPORT_RUN_STATUS_LABELS[run.status]}</StatusBadge>
              </div>
              <p className="text-xs text-muted-foreground">
                {t(`reports.schedules.triggers.${run.trigger}`)} · {formatDateTime(run.startedAt)} ·{" "}
                {REPORT_FORMAT_LABELS[run.format]}
              </p>
              {run.error ? <p className="text-xs text-destructive">{run.error}</p> : null}
              {run.recipients.length > 0 ? (
                <ul className="grid gap-1 text-sm">
                  {run.recipients.map((recipient) => (
                    <li key={recipient.userId} className="flex flex-wrap items-center gap-2">
                      <StatusBadge
                        tone={
                          recipient.status === "SENT"
                            ? "success"
                            : recipient.status === "FAILED"
                              ? "danger"
                              : "neutral"
                        }
                      >
                        {t(`reports.schedules.recipientStatus.${recipient.status}`)}
                      </StatusBadge>
                      <span>{recipient.fullName}</span>
                      {recipient.reason ? (
                        <span className="text-xs text-muted-foreground">— {recipient.reason}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </Drawer>
  );
}
